import React from 'react'
import PropTypes from 'prop-types'
import MuiButton from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'

const SIZE_SX = {
  sm: { height: '36px', fontSize: '13px', padding: '0 12px', borderRadius: 'var(--radius-md)' },
  default: { height: '48px', fontSize: '15px', padding: '0 20px', borderRadius: 'var(--radius-md)' },
  lg: { height: '52px', fontSize: '15px', padding: '0 20px', borderRadius: 'var(--radius-md)' },
}

// iconOnly's own size scale — a fixed square, not the text-button height/
// padding/font scale above. Values match the two real sizes the 47-file
// button-shape migration actually uses (28px "RowIconBtn"/"+" vinculo-header
// buttons, 30px "eye/edit/delete" table-row action buttons) — not a new
// design decision, just carrying forward what was already there so migrated
// pages stay pixel-identical. border-radius is a literal 8px because none of
// the --radius-* tokens land on it (6px/10px are the nearest neighbors).
const ICON_SIZE_SX = {
  sm: { width: '28px', height: '28px', minWidth: '28px', padding: 0, borderRadius: '8px' },
  default: { width: '30px', height: '30px', minWidth: '30px', padding: 0, borderRadius: '8px' },
  lg: { width: '36px', height: '36px', minWidth: '36px', padding: 0, borderRadius: '8px' },
}

const VARIANT_PROPS = {
  primary: { variant: 'contained', disableElevation: true },
  danger: { variant: 'contained', color: 'error', disableElevation: true },
  secondary: { variant: 'outlined' },
  ghost: { variant: 'outlined' },
  text: { variant: 'text' },
  outline: { variant: 'outlined' },
  icon: { variant: 'text' },
  'icon-filled': { variant: 'text' },
}

const VARIANT_SX = {
  primary: {
    backgroundColor: 'var(--color-brand)',
    '&:hover': {
      backgroundColor: 'var(--color-brand-strong)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  // Not MUI's augmentColor-derived hover shade — it can't decompose a
  // var() string. color-mix() keeps the hover 100% token-driven instead.
  danger: {
    '&:hover': {
      backgroundColor: 'color-mix(in srgb, var(--vo-danger) 85%, black)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  secondary: {
    borderColor: 'var(--border-default)',
    color: 'var(--color-brand)',
    '&:hover': {
      borderColor: 'var(--border-default)',
      backgroundColor: 'var(--vo-mist)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  ghost: {
    borderColor: 'var(--border-default)',
    color: 'var(--color-brand)',
    '&:hover': {
      borderColor: 'var(--border-default)',
      backgroundColor: 'var(--vo-mist)',
      boxShadow: 'none',
    },
  },
  text: {
    color: 'var(--color-brand)',
    '&:hover': {
      backgroundColor: 'var(--vo-mist)',
      boxShadow: 'none',
    },
  },
  // Shape B (47-file migration): border+text match, brand-tinted hover wash.
  // The pre-migration files hovered to a static var(--vo-capri-50) — an
  // unrelated accent tint, not derived from primary — so --color-brand-8
  // (already defined for theme.js's "primaryLight8") is the reactive
  // equivalent: a real, tenant-colored version of the same "very light
  // wash" idea, not a literal recolor of capri-50.
  outline: {
    backgroundColor: 'white',
    borderColor: 'var(--color-brand)',
    color: 'var(--color-brand)',
    '&:hover': {
      backgroundColor: 'var(--color-brand-8)',
      borderColor: 'var(--color-brand)',
    },
  },
  // Shape C (47-file migration): transparent/neutral at rest, brand-colored
  // only on hover — real precedent (Pages/Assets/index.jsx's IconBtn) keeps
  // this neutral at rest specifically so a page full of them doesn't read as
  // "all buttons," matching every other icon: only reacts to the pointer.
  icon: {
    color: 'var(--text-muted)',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'var(--vo-info-10)',
      color: 'var(--color-brand)',
    },
  },
  // Real precedent (VendorList.jsx's ActionIconBtn, Assets/index.jsx's
  // IconBtn danger prop): the exact same shape as `icon`, hover swaps to
  // the danger palette instead of brand. Combine with the `danger` prop,
  // not a separate variant name, since it's a hover-color swap on the same
  // shape, not a different button.
  'icon-danger': {
    color: 'var(--text-muted)',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'var(--vo-danger-10)',
      color: 'var(--vo-danger)',
    },
  },
  // Shape D (47-file migration): brand-tinted background always on, not
  // just on hover — real precedent (Areas/Show, Assets/Show, Systems/Show's
  // small "+" vinculo-header buttons) had no hover state defined at all;
  // a light darken here is a deliberate, additive consistency fix (this
  // shape's REST state, the thing every pixel-baseline actually checks,
  // is unchanged), not a forced behavior nothing asked for.
  'icon-filled': {
    color: 'var(--color-brand)',
    backgroundColor: 'var(--color-brand-8)',
    '&:hover': {
      backgroundColor: 'var(--color-brand-12)',
    },
  },
}

export const Button = ({
  variant,
  size,
  iconOnly,
  danger,
  disabled,
  isLoading,
  startIcon,
  endIcon,
  children,
  sx,
  ...otherProps
}) => {
  const isDisabled = disabled || isLoading
  const resolvedVariant = iconOnly && variant === 'icon' && danger ? 'icon-danger' : variant
  const sizeSx = iconOnly ? ICON_SIZE_SX[size] : SIZE_SX[size]

  return (
    <MuiButton
      {...VARIANT_PROPS[variant]}
      disabled={isDisabled}
      startIcon={iconOnly ? undefined : (isLoading ? <CircularProgress size={16} color="inherit" /> : startIcon)}
      endIcon={iconOnly ? undefined : endIcon}
      sx={[{ ...sizeSx, ...VARIANT_SX[resolvedVariant] }, sx]}
      {...otherProps}
    >
      {iconOnly && isLoading ? <CircularProgress size={14} color="inherit" /> : children}
    </MuiButton>
  )
}

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'danger', 'text', 'outline', 'icon', 'icon-filled']),
  size: PropTypes.oneOf(['sm', 'default', 'lg']),
  // Square, padding-less icon button (Shape C/D from the button-shape
  // migration) — pair with variant="icon" (transparent, brand-on-hover) or
  // variant="icon-filled" (brand-tinted background always on). `size` picks
  // the fixed square dimension (see ICON_SIZE_SX) instead of the text-button
  // height/padding/font scale.
  iconOnly: PropTypes.bool,
  // Only meaningful with iconOnly + variant="icon": swaps the hover tint
  // from brand/info to the danger palette (real precedent: a delete action
  // inside an otherwise-identical row of view/edit icon buttons).
  danger: PropTypes.bool,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}

Button.defaultProps = {
  variant: 'primary',
  size: 'default',
  iconOnly: false,
  danger: false,
  disabled: false,
  isLoading: false,
  sx: {},
}
